import { axios } from '@/lib/axios';
import { quizLocales } from '@/features/Quiz/utils/quizLocales';

/** Locale code of the source language. Quizzes in any other locale are copies of one. */
export const SOURCE_LOCALE = 'en';

/** Languages an English library quiz can be synced into, in UI order. */
export const SYNC_TARGET_LOCALES = [
    'cn',
    'kr',
    'fr',
    'it',
    'es',
    'de',
    'pt',
    'ja',
    'tr',
    'ar',
    'nl',
    'he',
    'pl',
    'ru',
] as const;

export const libraryQuizSyncStatusPath = (quizUuid: string): string =>
    `/quiz/library/${quizUuid}/sync-status`;

/** Articles Quiz Template tab — keep the library tab URL separate. */
export const articleQuizSyncStatusPath = (quizUuid: string): string =>
    `/quiz/${quizUuid}/sync-status`;

/** After saving an English library quiz, open the matching sync page instead of the list. */
export const afterLibraryQuizSavePath = (
    uuid: string | undefined,
    locale: string | null | undefined,
    libraryType: string | undefined,
    listPath: string,
): string => {
    if (!uuid || !isSourceLocale(locale)) {
        return listPath;
    }
    return libraryType === 'VIDEO' ? articleQuizSyncStatusPath(uuid) : libraryQuizSyncStatusPath(uuid);
};

export type QuizSyncAcceptance = 'STARTED' | 'NOTHING_TO_DO';

export type QuizSyncAccepted = {
    quizUuid: string;
    batchId?: string | null;
    requestId?: string | null;
    acceptance: QuizSyncAcceptance;
    locales: string[];
};

export const ALREADY_IN_SYNC_TOAST = 'In sync. No sync required';

export const SYNC_ALREADY_RUNNING_TOAST = 'A sync is already running for this quiz';

export const quizSyncToastTitle = (accepted?: Pick<QuizSyncAccepted, 'acceptance'> | null): string => {
    if (accepted?.acceptance === 'NOTHING_TO_DO') {
        return ALREADY_IN_SYNC_TOAST;
    }
    if (accepted?.acceptance === 'STARTED') {
        return 'Translation sync started';
    }
    return 'Translation sync requested';
};

export const syncDidWork = (accepted?: Pick<QuizSyncAccepted, 'acceptance'> | null): boolean =>
    accepted?.acceptance === 'STARTED';

type SyncErrorBody = {
    message?: string;
    content?: { message?: string };
};

/**
 * quiz-svc sends `{ message }` with no `content` wrapper. The shared axios interceptor
 * only reads `content.message`, so a 409 otherwise surfaces as the raw status text.
 */
export const quizSyncErrorTitle = (error: unknown): string => {
    const axiosError = error as {
        message?: string;
        response?: { status?: number; data?: SyncErrorBody };
    } | null;
    const data = axiosError?.response?.data;
    const message = data?.content?.message ?? data?.message;
    if (axiosError?.response?.status === 409) {
        return message || SYNC_ALREADY_RUNNING_TOAST;
    }
    if (message) {
        return message;
    }
    if (axiosError?.message && !axiosError.message.startsWith('Request failed with status code')) {
        return axiosError.message;
    }
    return 'Sync failed';
};

export type QuizSyncStatusCode =
    | 'PENDING'
    | 'IN_SYNC'
    | 'PARTIAL'
    | 'FAILED'
    | 'SOFT_DELETED'
    | 'NOT_SYNCED'
    | 'OUT_OF_DATE';

export type LocaleSyncStatus = {
    locale: string;
    displayName: string;
    quizUuid: string | null;
    status: QuizSyncStatusCode;
    questionCount: number;
    inSync: number;
    pending: number;
    manual: number;
    needsReview: number;
    failed: number;
    /** Questions a sync would translate or retire if it ran right now. */
    outOfDate: number;
    lastAppliedAt: string | null;
    lastErrorCode: string | null;
};

export type QuizSyncStatus = {
    quizUuid: string;
    syncInFlight: boolean;
    locales: LocaleSyncStatus[];
};

/**
 * True when this quiz is an English source rather than one of its translations.
 *
 * Quizzes created before locales were recorded have none, and those are English, which is
 * why an absent locale counts as a match rather than a miss.
 */
export const isSourceLocale = (locale?: string | null): boolean =>
    !locale || locale.toLowerCase() === SOURCE_LOCALE;

/**
 * Quiz-svc replies are proxied through unchanged. The shared interceptor unwraps a
 * `content` envelope when the response has one and hands back the whole axios response
 * when it does not, so accept either shape rather than betting on one.
 */
export const unwrapQuizSyncPayload = <T extends Record<string, unknown>>(response: unknown): T => {
    if (response && typeof response === 'object') {
        const body = response as Record<string, unknown>;
        if (body.quizUuid || body.acceptance) {
            return body as T;
        }
        if (body.data && typeof body.data === 'object') {
            return body.data as T;
        }
    }
    return response as T;
};

const localeDisplayName = (locale: string): string =>
    quizLocales.find((item) => item.key === locale)?.name ?? locale;

/**
 * What to show for a language, given both what the last run did and what English says now.
 *
 * A finished run leaves IN_SYNC behind and nothing ever revises it, so a language whose
 * English has since moved would keep claiming to be current. `outOfDate` is the live
 * answer, and it takes precedence over any settled state. It does not override PENDING or
 * FAILED: a run in progress is already working through exactly that drift, and a failure is
 * the more useful thing to put in front of whoever is looking. Nor NOT_SYNCED: every English
 * question counts as out of date for a language that has never been translated, which makes
 * an untouched quiz look like it has drifted when really there is nothing there yet.
 */
export const displayLocaleSyncStatus = (row: Pick<LocaleSyncStatus, 'status' | 'outOfDate'>): QuizSyncStatusCode => {
    if (
        row.status === 'PENDING' ||
        row.status === 'FAILED' ||
        row.status === 'SOFT_DELETED' ||
        row.status === 'NOT_SYNCED'
    ) {
        return row.status;
    }
    return row.outOfDate > 0 ? 'OUT_OF_DATE' : row.status;
};

/** Fills languages the API omitted, which should no longer happen but costs little to keep. */
export const mergeLocaleStatuses = (locales: LocaleSyncStatus[] = []): LocaleSyncStatus[] => {
    const byLocale = new Map(locales.map((row) => [row.locale.toLowerCase(), row]));
    return SYNC_TARGET_LOCALES.map((locale) => {
        const existing = byLocale.get(locale);
        if (existing) {
            return { ...existing, status: displayLocaleSyncStatus(existing) };
        }
        return {
            locale,
            displayName: localeDisplayName(locale),
            quizUuid: null,
            status: 'NOT_SYNCED',
            questionCount: 0,
            inSync: 0,
            pending: 0,
            manual: 0,
            needsReview: 0,
            failed: 0,
            outOfDate: 0,
            lastAppliedAt: null,
            lastErrorCode: null,
        };
    });
};

/** Omitting `locales` syncs every supported language, which is the usual case. */
export const syncLibraryQuizV2 = async (
    uuid: string,
    locales?: string[]
): Promise<QuizSyncAccepted> => {
    const response: unknown = await axios.post(
        `/api/v2/admin/quiz/library/${uuid}/sync`,
        locales ? { locales } : {}
    );
    return unwrapQuizSyncPayload<QuizSyncAccepted>(response);
};

export const getLibraryQuizSyncStatusV2 = async (uuid: string): Promise<QuizSyncStatus> => {
    const response: unknown = await axios.get(`/api/v2/admin/quiz/library/${uuid}/sync`);
    return unwrapQuizSyncPayload<QuizSyncStatus>(response);
};
